import { z } from 'zod';
import { createSchema } from 'zod-openapi';
import { SenioritySchema } from './vacancy-criteria.dto';

// ============================================
// M00 · CONFIGURATION & MASTER CATALOGS
// ============================================

/**
 * The master catalogs (M00 §2). The spec names one table per catalog; they are
 * stored in `catalog_items` discriminated by this type, and the API addresses
 * them as `/config/catalogs/:type`.
 *
 * The second group comes from the client's "Matriz Maestra para Reglas de
 * Vacantes" (12-ago-2026) and its §10, which is a list of exactly this: the
 * fields Development has to make administrable. Their reason, verbatim: «No
 * quiero que estas listas críticas queden enterradas en código. Deben poder
 * mantenerse/ampliarse sin tener que rehacer la lógica de la plataforma».
 *
 * Two of them are not lists of nouns and deserve saying out loud:
 *
 * - `auto_discard_fields` is the WHITELIST of criteria allowed to eliminate a
 *   candidate outright. Its whole point is that it is Kairos', not the
 *   client's: «Los campos que admiten auto-discard deben estar controlados por
 *   sistema». See `VacancyRules`.
 * - `role_aliases` and `equivalences` carry their pairing in `metadata`
 *   (`{ canonical }` / `{ equivalentTo }`), which is what lets "Micros" and
 *   "Simphony" send a candidate to manual review instead of the bin.
 */
export const CATALOG_TYPES = [
	'positions',
	'departments',
	'languages',
	'nationalities',
	'cities',
	'property_types',
	'luxury_levels',
	// Matriz Maestra §10.
	'seniority_levels',
	'specializations',
	'brands',
	'systems',
	'certifications',
	'operation_types',
	'role_aliases',
	'equivalences',
	'auto_discard_fields',
] as const;

export type CatalogType = (typeof CATALOG_TYPES)[number];

/** Level scale attached to `languages` items (M00 §2). */
export const LANGUAGE_PROFICIENCY_LEVELS = ['basic', 'intermediate', 'fluent', 'native'] as const;

/**
 * The weighted blocks of the K-Score. They are NOT declared here: the source of
 * truth is the active candidate `ScoringModel`, the very definition the engine
 * scores with. `GET /config/kscore` returns its blocks and `PATCH` publishes a
 * new version — otherwise the weights screen would be editing a table nobody
 * computes with.
 */

/** The total every weight set must add up to (M00 §3). */
export const KSCORE_WEIGHT_TOTAL = 100;

/** Sections of `settings_global` (M00 §2). One row per key. */
export const MERIT_SETTING_KEYS = [
	'validation_rules',
	'window_72h',
	'talent_access',
	'integrations',
	'security',
	'matching',
] as const;

export type MeritSettingKey = (typeof MERIT_SETTING_KEYS)[number];

// ============================================
// CATALOGS
// ============================================

export const CatalogItemSchema = z.object({
	id: z.number().int(),
	type: z.enum(CATALOG_TYPES),
	label: z.string(),
	slug: z.string(),
	order: z.number().int(),
	active: z.boolean(),
	metadata: z.record(z.string(), z.unknown()).nullable().optional(),
	/**
	 * Only on `positions`: the seniority level of the role, which is what decides
	 * whether a plan may publish it (Jumeirah 13-ago). It travels with the catalog
	 * so the vacancy form can SHOW the level it derived instead of asking for it —
	 * the company does not choose this, but it should not have to discover it by
	 * being refused. `null` = not classified yet.
	 */
	seniorityTier: SenioritySchema.nullable().optional(),
});

export type CatalogItemDTO = z.infer<typeof CatalogItemSchema>;

/**
 * New catalog value. `slug` is derived from the label when omitted, so the
 * admin only has to type the label.
 */
export const CreateCatalogItemSchema = z.object({
	/**
	 * Solo para `positions`: el nivel del puesto (Jumeirah 13-ago). Es lo que
	 * Kairos rellena para desbloquear un puesto que la clienta dejó sin
	 * clasificar, y por eso se edita por el CRUD del catálogo y no por una
	 * pantalla aparte: el nivel es un atributo del puesto, no una cola.
	 */
	seniorityTier: SenioritySchema.nullish().meta({
		title: 'Seniority tier',
		description: 'Only for positions: which plans may publish this role',
		example: 'management',
	}),
	label: z.string().trim().min(1).max(160).meta({
		title: 'Label',
		description: 'Value shown in the app',
		example: 'Front Office Manager',
	}),
	slug: z
		.string()
		.trim()
		.min(1)
		.max(160)
		.regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'slug must be kebab-case')
		.optional()
		.meta({ title: 'Slug', description: 'Stable identifier; derived from the label when omitted' }),
	order: z.number().int().min(0).optional().meta({ title: 'Order' }),
	active: z.boolean().optional().meta({ title: 'Active' }),
	metadata: z.record(z.string(), z.unknown()).optional().nullable().meta({ title: 'Extra data' }),
});

export type CreateCatalogItemDTO = z.infer<typeof CreateCatalogItemSchema>;

export const UpdateCatalogItemSchema = CreateCatalogItemSchema.partial();

export type UpdateCatalogItemDTO = z.infer<typeof UpdateCatalogItemSchema>;

// ============================================
// K-SCORE WEIGHTS
// ============================================

/**
 * Weight editor payload (M00 §3, §6). The keys are whatever blocks the active
 * model declares — the service checks they all exist and that none is missing,
 * since only it knows the current model. Here we enforce what is universal:
 * no duplicates and a total of 100, the same rule the UI enforces live.
 */
export const UpdateKScoreWeightsSchema = z.object({
	dimensions: z
		.array(
			z.object({
				key: z.string().min(1),
				weight: z.number().int().min(0).max(100),
			}),
		)
		.min(1)
		.refine((dims) => new Set(dims.map((d) => d.key)).size === dims.length, 'each block may only appear once')
		.refine(
			(dims) => dims.reduce((total, d) => total + d.weight, 0) === KSCORE_WEIGHT_TOTAL,
			`weights must add up to ${KSCORE_WEIGHT_TOTAL}`,
		)
		.meta({ title: 'Block weights' }),
});

export type UpdateKScoreWeightsDTO = z.infer<typeof UpdateKScoreWeightsSchema>;

// ============================================
// PLANS & ADD-ONS
// ============================================

/**
 * Editable fields of a plan (M00 §4). `key` is immutable: subscriptions point
 * at it. `null` in a limit means variable/unlimited.
 */
/**
 * Identity of a plan. The per-mode numbers do NOT live here — a plan has two
 * products with their own prices and their own entitlements, so they are edited
 * through {@link UpdatePlanPricingSchema} and {@link UpdatePlanEntitlementsSchema}.
 *
 * The six flat columns (`priceAed`, `activeRoles`…) are the pre-Monthly/Annual
 * model. They stay editable while the callers that still read them are
 * migrated, mirroring the ANNUAL mode.
 */
export const UpdatePlanSchema = z
	.object({
		name: z.string().trim().min(1).max(120),
		priceAed: z.number().int().min(0),
		recommended: z.boolean(),
		activeRoles: z.number().int().min(0).nullable(),
		annualRoles: z.number().int().min(0),
		videosPerMonth: z.number().int().min(0).nullable(),
		replacementDays: z.number().int().min(0),
		earlyAccessWindow: z.boolean(),
		order: z.number().int().min(0),
		active: z.boolean(),
		/** Quoted per contract: no price, no checkout, no inherited limits. */
		isEnterprise: z.boolean(),
		/** The card shows "Contact Sales" instead of a price. */
		contactSales: z.boolean(),
	})
	.partial();

export type UpdatePlanDTO = z.infer<typeof UpdatePlanSchema>;

/** Price of one plan in one billing mode. `null` price = quoted (ENTERPRISE). */
export const UpdatePlanPricingSchema = z
	.object({
		priceAed: z.number().int().min(0).nullable(),
		cycleDays: z.number().int().min(1).max(366).nullable(),
		durationMonths: z.number().int().min(1).max(60).nullable(),
		stripePriceId: z.string().trim().max(120).nullable(),
	})
	.partial();

export type UpdatePlanPricingDTO = z.infer<typeof UpdatePlanPricingSchema>;

/**
 * What a plan grants in one billing mode.
 *
 * Every nullable field means UNLIMITED when null, never zero: leaving Video
 * Requests empty is what PRIME's "unlimited (fair use)" is made of, and reading
 * it as 0 would lock the plan out of a feature it pays for. Zero is a real
 * value and means "included at zero", which is ACCESS's video allowance.
 */
export const UpdatePlanEntitlementsSchema = z
	.object({
		vacancyCredits: z.number().int().min(0).nullable(),
		activeVacancies: z.number().int().min(0).nullable(),
		contactUnlocks: z.number().int().min(0).nullable(),
		invitations: z.number().int().min(0).nullable(),
		videoRequests: z.number().int().min(0).nullable(),
		shortlists: z.number().int().min(0).nullable(),
		shortlistCandidates: z.number().int().min(1).max(20),
		seats: z.number().int().min(0).nullable(),
		venues: z.number().int().min(0).nullable(),
		priority: z.enum(['first', 'priority', 'standard']),
		/** Hours eligible talent stays invisible: PRIME 0, PROFESSIONAL 72, ACCESS 96. */
		priorityDelayHours: z.number().int().min(0).max(720),
		replacementDays: z.number().int().min(0).max(365),
		/** PTD por categoría para este plan y modalidad (04/09). Null = lista general. */
		ptdOperationalAed: z.number().int().min(0).nullable(),
		ptdSupervisorAed: z.number().int().min(0).nullable(),
		ptdManagementAed: z.number().int().min(0).nullable(),
		ptdSeniorDirectorAed: z.number().int().min(0).nullable(),
	})
	.partial();

export type UpdatePlanEntitlementsDTO = z.infer<typeof UpdatePlanEntitlementsSchema>;

/** Ceilings that force the account up to ENTERPRISE instead of more add-ons. */
export const UpdatePlanCapsSchema = z
	.object({
		vacancies: z.number().int().min(0).nullable(),
		contactUnlocks: z.number().int().min(0).nullable(),
		invitations: z.number().int().min(0).nullable(),
		shortlists: z.number().int().min(0).nullable(),
		seats: z.number().int().min(0).nullable(),
		venues: z.number().int().min(0).nullable(),
	})
	.partial();

export type UpdatePlanCapsDTO = z.infer<typeof UpdatePlanCapsSchema>;

export const UpdateAddonSchema = z
	.object({
		name: z.string().trim().min(1).max(120),
		description: z.string().trim().max(500).nullable(),
		priceAed: z.number().int().min(0),
		order: z.number().int().min(0),
		active: z.boolean(),
		/** Plan it belongs to; `null` = sold on every plan. */
		planKey: z.string().trim().max(40).nullable(),
		/** What buying it grants (ADDON_EFFECTS); `null` = nothing metered. */
		effect: z.string().trim().max(40).nullable(),
		/** Units granted per purchase — 10 unlocks, 25 invitations, 1 seat… */
		quantity: z.number().int().min(1).max(1000),
		/** Price on an annual term; `null` = not sold annually. */
		priceAnnualAed: z.number().int().min(0).nullable(),
		/** Purchases allowed per monthly cycle; `null` = not offered monthly. */
		cycleLimit: z.number().int().min(0).max(100).nullable(),
		/** Purchases allowed per annual term; `null` = not offered annually. */
		annualLimit: z.number().int().min(0).max(1000).nullable(),
	})
	.partial();

export type UpdateAddonDTO = z.infer<typeof UpdateAddonSchema>;

// ============================================
// GLOBAL SETTINGS
// ============================================

/** Queue SLA and validation rules (`validation_rules`). */
export const ValidationRulesSchema = z.object({
	candidateSlaHours: z.number().int().min(1).max(720),
	companySlaHours: z.number().int().min(1).max(720),
	commentRequiredOnRejection: z.boolean(),
	commentRequiredOnObservations: z.boolean(),
	luxuryReadyThreshold: z.number().int().min(0).max(100),
	highPotentialThreshold: z.number().int().min(0).max(100),
});

/** Premium Talent Window parameters (`window_72h`), consumed by M08. */
export const Window72hSchema = z.object({
	enabled: z.boolean(),
	durationHours: z.number().int().min(1).max(336),
	minKScore: z.number().int().min(0).max(100),
	plansWithAccess: z.array(z.string()),
});

/**
 * Talent-base access rules (`talent_access`): the base is browsable only while
 * the company has an active vacancy (client answer of 05/08).
 *
 * `profilesPerVacancy` used to live here — the 15-unlocks-per-vacancy allowance.
 * The commercial model of 9-ago replaced it with Contact Unlocks, a balance of
 * the PLAN spent once per candidate and company, so the setting stopped being
 * read by anything. It is gone rather than left in place: a number an admin can
 * still edit and nothing enforces is the worst of the two options.
 */
export const TalentAccessSettingsSchema = z.object({
	requireActiveVacancy: z.boolean(),
	/**
	 * Qué planes pueden filtrar el pool por los criterios avanzados (nivel de
	 * lujo y estado legal). El resto de filtros —puesto, disponibilidad,
	 * idiomas— los tiene todo el mundo y no se configuran.
	 *
	 * Vive aquí, y no en un `if` del servicio de talento, porque la lista la
	 * cambia la clienta: hasta hoy era `planKey !== 'access' && planKey !==
	 * 'freemium'` escrito a mano, así que abrirle un filtro a ACCESS —o cerrarle
	 * uno a PROFESSIONAL— era un despliegue. Es la misma forma que
	 * `window_72h.plansWithAccess`, que ya resuelve la misma pregunta para la
	 * ventana premium.
	 *
	 * Se guardan CLAVES DE PLAN y no una bandera por plan: el catálogo puede
	 * crecer, y un plan nuevo que nadie ha añadido a la lista se queda fuera de
	 * los filtros avanzados, que es el lado seguro del error.
	 *
	 * Lista vacía = ningún plan, y es una configuración legítima (apagar los
	 * filtros avanzados para todos). El servidor la respeta tal cual; no se
	 * reinterpreta como "todos", que es como una lista borrada sin querer
	 * regalaría el filtro al tier gratuito.
	 */
	advancedFilterPlans: z.array(z.string().trim().min(1)).max(50),
});

/** Read-only view of the existing integrations (`integrations`). */
export const IntegrationsSettingsSchema = z.object({
	crmEnabled: z.boolean(),
	crmProvider: z.string().max(60).nullable(),
	emailProvider: z.string().max(60).nullable(),
	invitationEmailEnabled: z.boolean(),
});

/** General security parameters (`security`). */
export const SecuritySettingsSchema = z.object({
	mfaRequiredForAdmins: z.boolean(),
	sessionTimeoutMinutes: z.number().int().min(5).max(1440),
	passwordMinLength: z.number().int().min(8).max(128),
});

/**
 * Matching (T4, 02/09): el interruptor de «puestos cercanos». Con él encendido,
 * el cruce vacante↔candidato y el tablón consideran también los puestos
 * adyacentes del mapa `ADJACENT_POSITIONS` (positions.catalog); apagado, solo
 * los puestos objetivo declarados. Pedido literal de la clienta: «probar
 * puestos cercanos en matching (con interruptor)».
 */
export const MatchingSettingsSchema = z.object({
	adjacentEnabled: z.boolean(),
});

/** Per-key value shape, so a PATCH can be validated against its section. */
export const MERIT_SETTING_SCHEMAS = {
	validation_rules: ValidationRulesSchema,
	window_72h: Window72hSchema,
	talent_access: TalentAccessSettingsSchema,
	integrations: IntegrationsSettingsSchema,
	security: SecuritySettingsSchema,
	matching: MatchingSettingsSchema,
} as const;

/** Defaults seeded on first run; also the fallback when a row is missing. */
export const MERIT_SETTING_DEFAULTS: {
	validation_rules: z.infer<typeof ValidationRulesSchema>;
	window_72h: z.infer<typeof Window72hSchema>;
	talent_access: z.infer<typeof TalentAccessSettingsSchema>;
	integrations: z.infer<typeof IntegrationsSettingsSchema>;
	security: z.infer<typeof SecuritySettingsSchema>;
	matching: z.infer<typeof MatchingSettingsSchema>;
} = {
	validation_rules: {
		candidateSlaHours: 48,
		companySlaHours: 24,
		commentRequiredOnRejection: true,
		commentRequiredOnObservations: true,
		luxuryReadyThreshold: 85,
		highPotentialThreshold: 70,
	},
	window_72h: {
		enabled: true,
		durationHours: 72,
		// 80, not 85: the client fixed the Premium Talent Window threshold on
		// 9-ago (MD-Planes-Configuracion §5). The seed moves an untouched row to
		// the new value and leaves an edited one alone.
		minKScore: 80,
		plansWithAccess: ['prime'],
	},
	talent_access: {
		requireActiveVacancy: true,
		// Exactamente lo que hacía el `if` que sustituye: todos menos ACCESS y
		// FREEMIUM. Se enumera en positivo porque la lista guardada es una lista
		// de permitidos, y una denylist y una allowlist con el mismo contenido
		// dejan de coincidir en cuanto aparece un plan nuevo.
		advancedFilterPlans: ['professional', 'prime', 'enterprise'],
	},
	integrations: {
		crmEnabled: false,
		crmProvider: null,
		emailProvider: 'resend',
		invitationEmailEnabled: true,
	},
	security: {
		mfaRequiredForAdmins: false,
		sessionTimeoutMinutes: 480,
		passwordMinLength: 8,
	},
	// Encendido de serie: la clienta pidió PROBARLO; el interruptor existe para
	// apagarlo si el resultado no le convence, sin despliegue.
	matching: {
		adjacentEnabled: true,
	},
};

/**
 * PATCH /config/settings — a partial map of section → value. Each section is
 * validated against its own schema in the service, since the shape depends on
 * the key.
 */
export const UpdateMeritSettingsSchema = z
	.object({
		validation_rules: ValidationRulesSchema.partial(),
		window_72h: Window72hSchema.partial(),
		// Faltaba: la sección existía, se leía y no había forma de escribirla.
		talent_access: TalentAccessSettingsSchema.partial(),
		integrations: IntegrationsSettingsSchema.partial(),
		security: SecuritySettingsSchema.partial(),
		matching: MatchingSettingsSchema.partial(),
	})
	.partial()
	.refine((body) => Object.keys(body).length > 0, 'at least one settings section is required');

export type UpdateMeritSettingsDTO = z.infer<typeof UpdateMeritSettingsSchema>;

// ============================================
// Internal staff roles (M00 §2 · admin_roles)
// ============================================

/**
 * The four-level internal ladder. It is a closed list on purpose: these roles
 * are the ones the Ajustes screen may edit, and `root`/`admin` plus the portal
 * roles (`candidate`, `company_owner`, `company_member`) are deliberately out —
 * the portals' guards depend on the latter.
 */
export const ADMIN_ROLE_NAMES = ['owner', 'lead_l3', 'validator_l2', 'reviewer_l1'] as const;

export type AdminRoleName = (typeof ADMIN_ROLE_NAMES)[number];

/**
 * Full replacement of a role's permissions: the editor always sends the whole
 * set it has on screen, so there is no partial state to reconcile. An empty
 * array is allowed — a role with nothing granted is a legitimate (if useless)
 * configuration, and refusing it would be the API second-guessing the admin.
 */
export const UpdateAdminRolePermissionsSchema = z.object({
	permissions: z.array(z.string().trim().min(1)).max(500),
});

export type UpdateAdminRolePermissionsDTO = z.infer<typeof UpdateAdminRolePermissionsSchema>;

// ============================================
// OpenAPI
// ============================================

export const CatalogItemOpenAPI = createSchema(CatalogItemSchema).schema;
export const CreateCatalogItemOpenAPI = createSchema(CreateCatalogItemSchema).schema;
export const UpdateCatalogItemOpenAPI = createSchema(UpdateCatalogItemSchema).schema;
export const UpdateKScoreWeightsOpenAPI = createSchema(UpdateKScoreWeightsSchema).schema;
export const UpdatePlanOpenAPI = createSchema(UpdatePlanSchema).schema;
export const UpdatePlanPricingOpenAPI = createSchema(UpdatePlanPricingSchema).schema;
export const UpdatePlanEntitlementsOpenAPI = createSchema(UpdatePlanEntitlementsSchema).schema;
export const UpdatePlanCapsOpenAPI = createSchema(UpdatePlanCapsSchema).schema;
export const UpdateAddonOpenAPI = createSchema(UpdateAddonSchema).schema;
export const UpdateMeritSettingsOpenAPI = createSchema(UpdateMeritSettingsSchema).schema;
export const UpdateAdminRolePermissionsOpenAPI = createSchema(UpdateAdminRolePermissionsSchema).schema;
